import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Pin,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Download,
  Film,
  PlayCircle,
  Copy,
  Link2,
  ShoppingCart,
  Zap,
  Gift,
  Bot,
  ExternalLink,
  LayoutGrid
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Pinterest» (solo admin): pines de vídeo con datos reales que llevan a las webs propias. Solo /api/admin/pin/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/pin`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  planning: { label: "Buscando datos", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  uploading: { label: "Publicando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  ready: { label: "Listo, sin publicar", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "En Pinterest", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const SOURCE_ICON = { comparacesta: ShoppingCart, prisa: Zap, elfo: Gift };

const Copyable = ({ value, label }) => {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 min-w-0 truncate bg-black/50 border border-white/10 rounded px-2 py-1 text-[11px] text-rose-200">{value}</code>
      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 2000); }}
        className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 shrink-0">
        <Copy className="w-3 h-3" /> {done ? "Copiado" : label || "Copiar"}
      </Button>
    </div>
  );
};

export const PinterestView = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [boards, setBoards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [appCreds, setAppCreds] = useState({ client_id: "", client_secret: "" });
  const [pasted, setPasted] = useState("");
  const [sandbox, setSandbox] = useState(true);
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, { params: { origin: window.location.origin }, ...opts() });
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar la conexión con Pinterest." });
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`, opts());
      setConfig(res.data);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadBoards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/boards`, opts());
      setBoards(res.data.boards);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudieron leer los tableros." });
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadVideos();
    const onMsg = (ev) => {
      if (ev.data?.type === "PIN_CONNECTED") {
        setWorking(null);
        setFeedback({ type: "success", text: ev.data.message || "Pinterest conectado." });
        loadStatus();
      } else if (ev.data?.type === "PIN_ERROR") {
        setWorking(null);
        setFeedback({ type: "error", text: ev.data.message || "Pinterest rechazó la conexión." });
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus, loadConfig, loadVideos]);

  const isConnected = !!status?.connected;
  useEffect(() => {
    if (isConnected) loadBoards();
  }, [isConnected, loadBoards]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy);
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      const msg = await fn();
      if (okText || msg) setFeedback({ type: "success", text: msg || okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveApp = () => run("app", async () => {
    await axios.post(`${API}/account/app`, appCreds, opts());
    setAppCreds({ client_id: "", client_secret: "" });
    await loadStatus();
  }, "App de Pinterest guardada. Ahora pulsa «Conectar con Pinterest».");

  const connect = async () => {
    setFeedback(null);
    setWorking("connect");
    try {
      const res = await axios.get(`${API}/account/auth-url`, { params: { origin: window.location.origin, sandbox }, ...opts() });
      const popup = window.open(res.data.auth_url, "cortoviral_pin_oauth", "width=560,height=780,left=200,top=80,resizable=yes,scrollbars=yes");
      if (!popup) {
        window.location.href = res.data.auth_url;
        return;
      }
      setFeedback({ type: "info", text: "En la ventana de Pinterest entra con tu cuenta y acepta los permisos. Al terminar, esta pantalla se actualiza sola." });
      const t = setInterval(() => {
        if (popup.closed) {
          clearInterval(t);
          setWorking(null);
          loadStatus();
        }
      }, 800);
    } catch (e) {
      setWorking(null);
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error iniciando la conexión con Pinterest." });
    }
  };

  const saveToken = () => run("token", async () => {
    await axios.post(`${API}/account/token`, { access_token: pasted, sandbox }, opts());
    setPasted("");
    await loadStatus();
  }, "Token guardado. Caduca solo: es solo para probar.");

  const disconnect = () => {
    if (!window.confirm("¿Desconectar la cuenta de Pinterest? La app guardada se conserva para volver a conectar.")) return;
    run("disconnect", async () => {
      await axios.post(`${API}/account/disconnect`, {}, opts());
      await loadStatus();
    }, "Cuenta de Pinterest desconectada.");
  };

  const forgetApp = () => {
    if (!window.confirm("¿Borrar también el App ID y el App secret guardados?")) return;
    run("forget", async () => {
      await axios.post(`${API}/account/forget-app`, {}, opts());
      await loadStatus();
    }, "App de Pinterest borrada.");
  };

  const saveConfig = (patch) => run("config", async () => {
    const res = await axios.post(`${API}/config`, patch, opts());
    setConfig(res.data);
  });

  const makeBoards = () => run("boards", async () => {
    const res = await axios.post(`${API}/boards/suggested`, {}, opts());
    setConfig(res.data);
    await loadBoards();
  }, "Tableros listos y asignados.");

  const toggleIn = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const createVideo = (source, mode) => run(`create-${source}-${mode || ""}`, async () => {
    await axios.post(`${API}/videos/create`, { source, mode, publish: !!status?.connected }, opts());
    setBusy(true);
    loadVideos();
  }, status?.connected
    ? "Creando el pin. En un par de minutos estará en tu tablero."
    : "Creando el vídeo. Como Pinterest no está conectado, se quedará aquí para verlo y descargarlo.");

  const publishVideo = (v) => run(`pub-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/publish`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Publicado en Pinterest.");

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en Pinterest, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  if (!status || !config) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Pinterest...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = config.settings;
  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : feedback?.type === "info" ? "bg-blue-500/10 border border-blue-500/30 text-blue-200"
      : "bg-red-500/10 border border-red-500/30 text-red-300";
  const card = "bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3";
  const input = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-rose-500/60";
  const chip = (on) => `px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition-all ${
    on ? "bg-rose-600/20 border-rose-500/50 text-rose-200" : "bg-black/30 border-white/10 text-slate-400 hover:text-white"}`;

  return (
    <div data-testid="pin-view" className="space-y-6">
      {/* Conexión */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#2A0F14] border border-rose-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {status.connected && status.avatar_url ? (
              <img src={status.avatar_url} alt="" className="w-12 h-12 rounded-full border border-rose-500/40 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-rose-500/15 border border-rose-500/40 text-rose-300 flex items-center justify-center shrink-0">
                <Pin className="w-6 h-6" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Pinterest</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Cuenta: {status.username || "conectada"}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
                {status.sandbox && (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">Entorno de pruebas</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Pines de vídeo con datos reales de ComparaCesta, Prisa o Ahorro y Elfo Travieso. Cada pin lleva a tu web y, desde ahí, al enlace de afiliado.
                Va aparte de YouTube, no gasta créditos y no usa IA.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Pines en 24 h: {status.pins_today}/{status.max_pins}</span>
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}
        {status.last_error && <p className="text-[11px] text-red-400 break-words">Último error: {status.last_error}</p>}
        {status.sandbox && (
          <p className="text-[11px] text-amber-300">
            En el entorno de pruebas los pines son imágenes (no admite vídeo) y solo los ves tú. Cuando Pinterest apruebe tu app, reconecta sin esta opción.
          </p>
        )}

        {!status.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2 min-w-0">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-rose-300" /> App de Pinterest</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>Con una cuenta de empresa, crea una app en <strong className="text-slate-200">developers.pinterest.com</strong>.</li>
                <li>Permisos: {status.scopes.join(", ")}.</li>
                <li>En la app, añade esta dirección de vuelta (Redirect URI):</li>
              </ol>
              <Copyable value={status.redirect_uri} />
              <p className="text-slate-400">4. Las páginas legales que pide la app:</p>
              <Copyable value={status.site_urls.terms} label="Condiciones" />
              <Copyable value={status.site_urls.privacy} label="Privacidad" />
              <p className="text-slate-400">5. Pega aquí el App ID y el App secret. Son tuyos: no los compartas con nadie, tampoco en un chat.</p>
            </div>
            <div className="space-y-3">
              {status.has_app ? (
                <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
                  <p className="break-all">App de Pinterest guardada: <code className="text-rose-200">{status.client_id}</code></p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="pin-connect-btn" onClick={connect} disabled={working !== null}
                      className="bg-rose-600 hover:bg-rose-500 text-white font-black text-xs flex items-center gap-1.5">
                      {working === "connect" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                      Conectar con Pinterest
                    </Button>
                    <Button variant="ghost" size="sm" onClick={forgetApp} disabled={working !== null}
                      className="h-9 text-xs text-slate-400 hover:text-red-300">Cambiar app</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <input type="text" placeholder="App ID" value={appCreds.client_id} autoComplete="off"
                    onChange={(e) => setAppCreds((c) => ({ ...c, client_id: e.target.value }))} className={input} />
                  <input type="password" placeholder="App secret" value={appCreds.client_secret} autoComplete="new-password"
                    onChange={(e) => setAppCreds((c) => ({ ...c, client_secret: e.target.value }))} className={input} />
                  <Button data-testid="pin-save-app-btn" onClick={saveApp} disabled={working !== null || !appCreds.client_id || !appCreds.client_secret}
                    className="w-full bg-rose-600 hover:bg-rose-500 text-white font-black text-xs flex items-center justify-center gap-1.5">
                    {working === "app" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Guardar app de Pinterest
                  </Button>
                  <p className="text-[10px] text-slate-500">Se guardan solo en el servidor y nunca se devuelven al navegador.</p>
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} className="accent-rose-500 w-4 h-4" />
                Usar el entorno de pruebas (déjalo marcado hasta que aprueben tu app)
              </label>
              <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-400 space-y-2">
                <p>O pega un token de prueba del portal de Pinterest:</p>
                <div className="flex gap-2">
                  <input type="password" placeholder="Token de prueba" value={pasted} autoComplete="new-password"
                    onChange={(e) => setPasted(e.target.value)} className={input} />
                  <Button onClick={saveToken} disabled={working !== null || !pasted}
                    className="bg-slate-700 hover:bg-slate-600 text-white font-bold text-xs shrink-0">Usar token</Button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500">Sin conectar también puedes crear vídeos: se quedan aquí para verlos y descargarlos.</p>
            </div>
          </div>
        )}
      </div>

      {/* Tableros */}
      {status.connected && (
        <div className={card}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h4 className="text-sm font-black text-white flex items-center gap-1.5"><LayoutGrid className="w-4 h-4 text-rose-300" /> Tableros</h4>
            <Button onClick={makeBoards} disabled={working !== null}
              className="bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs flex items-center gap-1.5">
              {working === "boards" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <LayoutGrid className="w-3.5 h-3.5" />}
              Crear los tableros recomendados
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            {Object.entries(config.source_names).map(([k, label]) => (
              <label key={k} className="space-y-1 text-slate-300 font-bold">
                <span>{label}</span>
                <select value={(s.boards || {})[k] || ""} disabled={working !== null}
                  onChange={(e) => saveConfig({ boards: { ...(s.boards || {}), [k]: e.target.value } })}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white font-normal">
                  <option value="">Sin elegir</option>
                  {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Crear ahora */}
      <div className={card}>
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Film className="w-4 h-4 text-rose-300" /> Crear un pin ahora</h4>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => createVideo("elfo")} disabled={working !== null || busy}
            className="bg-red-700 hover:bg-red-600 text-white font-bold text-xs flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5" /> Elfo: una travesura
          </Button>
          <Button onClick={() => createVideo("prisa")} disabled={working !== null || busy}
            className="bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Prisa o Ahorro: un chollo
          </Button>
          <Button onClick={() => createVideo("comparacesta", "producto")} disabled={working !== null || busy}
            className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: un producto
          </Button>
          <Button onClick={() => createVideo("comparacesta", "cesta")} disabled={working !== null || busy}
            className="bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: una cesta
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">Se hace uno a la vez. Los datos salen en directo de tus webs.{busy && " Ahora mismo se está creando uno."}</p>
      </div>

      {/* Piloto */}
      <div className={card}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Bot className="w-4 h-4 text-rose-300" /> Piloto automático</h4>
          <button onClick={() => saveConfig({ autopilot_enabled: !s.autopilot_enabled })} disabled={working !== null}
            className={`px-3 py-1.5 rounded-lg text-xs font-black border ${s.autopilot_enabled
              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "bg-slate-800 border-white/10 text-slate-400"}`}>
            {s.autopilot_enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Crea pines solos, turnándose entre las fuentes activadas. Elfo Travieso solo entra de octubre a Nochebuena.
          {s.autopilot_enabled && config.next_autopilot && <> Próximo: <strong className="text-slate-200">{new Date(config.next_autopilot).toLocaleString("es-ES")}</strong>.</>}
        </p>
        {s.last_error && <p className="text-[11px] text-red-400 break-words">{s.last_error}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Pines al día</p>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => saveConfig({ pins_per_day: n })} disabled={working !== null} className={chip(s.pins_per_day === n)}>{n}</button>
              ))}
            </div>
            <p className="text-slate-300 font-bold pt-1">Fuentes</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(config.source_names).map(([k, label]) => {
                const Icon = SOURCE_ICON[k] || Film;
                return (
                  <button key={k} onClick={() => saveConfig({ sources: toggleIn(s.sources || [], k) })} disabled={working !== null} className={chip((s.sources || []).includes(k))}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Formato</p>
            <div className="flex gap-1.5">
              <button onClick={() => saveConfig({ pin_format: "video" })} disabled={working !== null} className={chip(s.pin_format === "video")}>Vídeo</button>
              <button onClick={() => saveConfig({ pin_format: "image" })} disabled={working !== null} className={chip(s.pin_format === "image")}>Imagen (portada)</button>
            </div>
            <label className="flex items-center gap-2 text-slate-300 pt-1">
              <input type="checkbox" checked={!!s.auto_publish} onChange={(e) => saveConfig({ auto_publish: e.target.checked })} className="accent-rose-500 w-4 h-4" />
              Publicar en Pinterest al terminar
            </label>
            <label className="block text-slate-300 font-bold pt-1">Código postal de ComparaCesta (opcional)</label>
            <input type="text" inputMode="numeric" maxLength={5} placeholder="Sin código: precios online generales" defaultValue={s.cc_postal_code || ""}
              onBlur={(e) => e.target.value !== (s.cc_postal_code || "") && saveConfig({ cc_postal_code: e.target.value })} className={input} />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className={card}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Pin className="w-4 h-4 text-rose-300" /> Pines ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={() => { loadVideos(); loadStatus(); }} className="h-8 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Actualizar
          </Button>
        </div>
        {videos.length === 0 && <p className="text-xs text-slate-500">Todavía no hay ninguno.</p>}
        <div className="space-y-2">
          {videos.map((v) => {
            const st = STATUS[v.status] || STATUS.failed;
            const Icon = SOURCE_ICON[v.source] || Film;
            return (
              <div key={v.id} data-testid="pin-video-row" className="p-3 rounded-xl bg-black/30 border border-white/5 space-y-2">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white flex items-center gap-1.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-rose-300 shrink-0" /><span className="truncate">{v.title}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 break-words">{v.progress}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${st.style}`}>{st.label}</span>
                    {v.video_url && !v.video_deleted && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3.5 h-3.5" /> Ver
                        </Button>
                        <a href={`${BACKEND_URL}${v.video_url}`} onClick={async (e) => {
                          e.preventDefault();
                          const res = await axios.get(`${BACKEND_URL}${v.video_url}`, { ...opts(), responseType: "blob" });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(res.data);
                          a.download = `pinterest-${v.id.slice(0, 8)}.mp4`;
                          a.click();
                        }} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" /> Bajar
                        </a>
                      </>
                    )}
                    {v.status === "ready" && status.connected && (
                      <Button size="sm" onClick={() => publishVideo(v)} disabled={working !== null}
                        className="h-7 px-2 bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold flex items-center gap-1">
                        <Upload className="w-3.5 h-3.5" /> Publicar
                      </Button>
                    )}
                    {v.pin_url && (
                      <a href={v.pin_url} target="_blank" rel="noopener noreferrer" className="h-7 px-2 text-[11px] text-emerald-300 hover:text-white flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" /> Abrir
                      </a>
                    )}
                    {!st.busy && (
                      <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null} className="h-7 px-2 text-slate-500 hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {v.error && v.status !== "failed" && <p className="text-[11px] text-amber-300 break-words">{v.error}</p>}
                {preview === v.id && <PinPreview video={v} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// El vídeo va con token en la cabecera, así que se baja como blob para enseñarlo
const PinPreview = ({ video }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${BACKEND_URL}${video.video_url}`, { ...opts(), responseType: "blob" }).then((res) => {
      url = URL.createObjectURL(res.data);
      setSrc(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [video.video_url]);
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {src ? <video src={src} controls className="w-40 rounded-lg border border-white/10 shrink-0" /> : <p className="text-xs text-slate-500">Cargando vídeo…</p>}
      <div className="text-xs text-slate-300 min-w-0 space-y-1">
        <p className="font-bold text-white break-words">{video.pin?.title}</p>
        <p className="whitespace-pre-wrap break-words text-slate-400">{video.pin?.description}</p>
        <p className="break-all text-rose-200">{video.pin?.link}</p>
      </div>
    </div>
  );
};
